const GKP_V2_BASE_URL = '/GlobalKOLPlanning';
const DEFAULT_CALENDAR_COLOR = '#5f6b7a';

const normalizeKey = value => `${value || ''}`.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

const resolveEventStableDimensionKey = dimension => {
  const normalizedKey = normalizeKey(dimension?.key || dimension?.label);

  if (dimension?.isCustomAttribute && dimension?.id !== undefined && dimension?.id !== null) {
    return `attribute:${dimension.id}`;
  }

  switch (normalizedKey) {
    case 'titleofevent':
    case 'eventname':
    case 'eventtitle':
      return 'eventTitle';
    case 'startdate':
      return 'startDate';
    case 'enddate':
      return 'endDate';
    case 'country':
    case 'eventcountry':
    case 'countryid':
      return 'countryId';
    case 'typeofevent':
    case 'type':
    case 'eventtypeid':
      return 'eventTypeId';
    case 'businessowner':
    case 'businessownerid':
      return 'businessOwnerId';
    default:
      return undefined;
  }
};

const resolveProfileStableDimensionKey = dimension => {
  const normalizedKey = normalizeKey(dimension?.key || dimension?.label);

  if (dimension?.isCustomAttribute && dimension?.id !== undefined && dimension?.id !== null) {
    return `attribute:${dimension.id}`;
  }

  switch (normalizedKey) {
    case 'profilename':
    case 'name':
      return 'profileName';
    case 'firstname':
      return 'firstName';
    case 'lastname':
      return 'lastName';
    case 'email':
    case 'emailaddress':
    case 'kolemail':
      return 'email';
    case 'country':
    case 'countryid':
      return 'countryId';
    default:
      return undefined;
  }
};

const resolveInteractionStableDimensionKey = dimension => {
  const normalizedKey = normalizeKey(dimension?.key || dimension?.label);

  if (dimension?.isCustomAttribute && dimension?.id !== undefined && dimension?.id !== null) {
    return `attribute:${dimension.id}`;
  }

  switch (normalizedKey) {
    case 'planneddate':
      return 'interactionDueDate';
    case 'actualdate':
      return 'interactionActualDate';
    case 'notes':
      return 'interactionNotes';
    case 'businessowner':
    case 'businessownerid':
      return 'businessOwnerId';
    case 'interactionstatus':
    case 'interactionstatusid':
      return 'interactionStatusId';
    case 'interactiontype':
    case 'interactiontypeid':
      return 'interactionTypeId';
    default:
      return undefined;
  }
};

const toStableDimensionKey = dimension => {
  const normalizedScope = normalizeKey(dimension?.scope);

  switch (normalizedScope) {
    case 'event':
      return resolveEventStableDimensionKey(dimension);
    case 'kol':
    case 'profile':
      return resolveProfileStableDimensionKey(dimension);
    case 'interaction':
      return resolveInteractionStableDimensionKey(dimension);
    default:
      return undefined;
  }
};

const toPopulationScope = scope => {
  const normalizedScope = normalizeKey(scope);

  switch (normalizedScope) {
    case 'kol':
      return 'profile';
    case 'event':
      return 'event';
    case 'interaction':
      return 'interaction';
    default:
      return scope;
  }
};

const normalizeType = type => {
  const normalizedType = `${type || ''}`.trim().toLowerCase();

  switch (normalizedType) {
    case 'event':
      return 'Event';
    case 'kol':
    case 'profile':
      return 'KOL';
    case 'interaction':
      return 'Interaction';
    case 'eventattendance':
    case 'attendee':
      return 'EventAttendance';
    default:
      return type;
  }
};

const normalizePeriodType = periodType => {
  const normalizedPeriodType = `${periodType || ''}`.trim().toLowerCase();

  switch (normalizedPeriodType) {
    case 'weekly':
      return 'week';
    case 'monthly':
      return 'month';
    case 'quarterly':
      return 'quarter';
    default:
      return normalizedPeriodType || periodType;
  }
};

const formatDisplayValue = value => {
  if (Array.isArray(value)) {
    return value.map(formatDisplayValue).filter(Boolean).join(', ');
  }

  if (value && typeof value === 'object') {
    return value.label || value.value || '';
  }

  return value ?? '';
};

const toTableValue = value => {
  if (Array.isArray(value)) {
    return value.map(item => formatDisplayValue(item)).filter(Boolean);
  }

  if (value && typeof value === 'object') {
    return value.label || value.value || '';
  }

  return value ?? '';
};

const isSingleOptionDataType = dataType =>
  ['dropdownsingle', 'radio'].includes(normalizeKey(dataType));

const isMultiOptionDataType = dataType =>
  ['dropdownmulti', 'checkbox'].includes(normalizeKey(dataType));

const toOptionObject = value => {
  if (!value) {
    return null;
  }

  if (Array.isArray(value)) {
    return toOptionObject(value[0]);
  }

  if (typeof value === 'object') {
    return value;
  }

  const label = formatDisplayValue(value);
  return label ? { label } : null;
};

const toOptionArray = value => {
  if (Array.isArray(value)) {
    return value.map(item => toOptionObject(item)).filter(Boolean);
  }

  if (value) {
    return [toOptionObject(value)].filter(Boolean);
  }

  return [];
};

const toLegacyAttributeKey = attribute => attribute?.attributeDefinitionId ?? attribute?.key;

const toLegacyColumnKey = column =>
  column?.isCustomAttribute && column?.attributeDefinitionId !== undefined
    ? column.attributeDefinitionId
    : column?.key;

const toLegacyAttributeTableValue = attribute => {
  if (isSingleOptionDataType(attribute?.dataType)) {
    return toOptionObject(attribute?.value);
  }

  if (isMultiOptionDataType(attribute?.dataType)) {
    return toOptionArray(attribute?.value);
  }

  return toTableValue(attribute?.value);
};

const toCustomAttributes = attributes =>
  (attributes || []).map(attribute => ({
    columnName: attribute.label,
    value: formatDisplayValue(attribute.value),
  }));

const withHexPrefix = value => {
  if (!value) {
    return value;
  }

  return `${value}`.startsWith('#') ? value : `#${value}`;
};

const buildTableRowItem = ({ key, value, id, tooltip }) => {
  const item = {
    key,
    value,
  };

  if (id !== undefined && id !== null && id !== '') {
    item.id = id;
  }

  if (tooltip) {
    item.tooltip = tooltip;
  }

  return item;
};

const uniqueColumns = columns => [
  ...new Map(columns.filter(Boolean).map(column => [column.key, column])).values(),
];

const buildAttributeColumns = items =>
  uniqueColumns(
    items.flatMap(item =>
      (item.attributes || []).map(attribute => ({
        key: toLegacyAttributeKey(attribute),
        label: attribute.label,
        dataType: attribute.dataType,
        sortOrder: attribute.sortOrder ?? null,
      })),
    ),
  );

const DEFAULT_EVENT_COLUMNS = [
  { key: 'EventTitle', label: 'Title', dataType: 'String', sortOrder: 0 },
  { key: 'StartDate', label: 'Start Date', dataType: 'Date', sortOrder: 1 },
  { key: 'EndDate', label: 'End Date', dataType: 'Date', sortOrder: 1 },
  { key: 'CountryID', label: 'Country', dataType: 'String', sortOrder: 2 },
  { key: 'EventTypeID', label: 'Type of Event', dataType: 'String', sortOrder: 3 },
  { key: 'BusinessOwnerID', label: 'Business Owner', dataType: 'String', sortOrder: 4 },
  { key: 'NumberOfAttendees', label: '# of Attendees', dataType: 'Number', sortOrder: 1 },
  { key: 'EventConflicts', label: 'Conflicts', dataType: 'ConflictBoolean', sortOrder: 2 },
];

const DEFAULT_PROFILE_COLUMNS = [
  { key: 'KOLName', label: 'Name', dataType: 'String', sortOrder: 0 },
  { key: 'KOLEvents', label: 'Events', dataType: 'Number', sortOrder: 1 },
  { key: 'interactions', label: 'Interactions', dataType: 'Number', sortOrder: 1 },
  { key: 'totalEngagements', label: 'Total Engagements', dataType: 'Number', sortOrder: 1 },
  { key: 'KOLHcpMaxEngagements', label: 'Event Cap', dataType: 'Number', sortOrder: 2 },
  { key: 'EventConflicts', label: 'Conflicts', dataType: 'ConflictBoolean', sortOrder: 3 },
];

const mapBackendColumns = response =>
  (response?.columns || []).map(column => ({
    key: toLegacyColumnKey(column),
    label: column.label,
    dataType: column.dataType,
    sortOrder: column.sortOrder ?? null,
  }));

const buildColumnsFromResponse = ({ response, items, fallbackColumns }) => {
  const backendColumns = mapBackendColumns(response);

  if (backendColumns.length > 0) {
    const backendKeys = new Set(backendColumns.map(column => `${column.key}`));
    const missingAttributeColumns = buildAttributeColumns(items).filter(
      column => !backendKeys.has(`${column.key}`),
    );

    return uniqueColumns([...backendColumns, ...missingAttributeColumns]);
  }

  return uniqueColumns([...fallbackColumns, ...buildAttributeColumns(items)]);
};

const buildEventColumnsFromResponse = (response, items) =>
  buildColumnsFromResponse({
    response,
    items,
    fallbackColumns: DEFAULT_EVENT_COLUMNS,
  });

const buildLegacyEventTableRow = (item, { includeCreatedBy = false } = {}) => [
  buildTableRowItem({ key: 'EventID', value: item.id }),
  buildTableRowItem({ key: 'EventTitle', value: item.title || '' }),
  buildTableRowItem({ key: 'StartDate', value: item.startDate || '' }),
  buildTableRowItem({ key: 'EndDate', value: item.endDate || '' }),
  buildTableRowItem({ key: 'CountryID', id: item.countryId, value: item.country || '' }),
  buildTableRowItem({ key: 'EventTypeID', id: item.eventTypeId, value: item.eventType || '' }),
  buildTableRowItem({
    key: 'BusinessOwnerID',
    id: item.businessOwnerId,
    value: item.businessOwner || '',
  }),
  buildTableRowItem({ key: 'NumberOfAttendees', value: item.attendeeCount || 0 }),
  buildTableRowItem({ key: 'EventConflicts', value: item.conflictCount || 0 }),
  buildTableRowItem({ key: 'AlertFrequency', value: item.alertFrequency ?? 0 }),
  ...(includeCreatedBy && item.createdBy
    ? [buildTableRowItem({ key: 'CreatedBy', value: item.createdBy })]
    : []),
  ...(item.attributes || []).map(attribute =>
    buildTableRowItem({
      key: toLegacyAttributeKey(attribute),
      value: toLegacyAttributeTableValue(attribute),
    }),
  ),
];

const buildEventTableResponse = response => {
  const items = response?.items || [];
  const columns = buildEventColumnsFromResponse(response, items);

  const tableData = items.map(item => buildLegacyEventTableRow(item));

  return { columns, tableData };
};

const getEventDetailStaticValue = (item, metadata) => {
  switch (normalizeKey(metadata?.key || metadata?.description)) {
    case 'eventtitle':
    case 'eventname':
    case 'titleofevent':
      return item.title || '';
    case 'startdate':
      return item.startDate || '';
    case 'enddate':
      return item.endDate || '';
    case 'country':
    case 'countryid':
    case 'eventcountry':
      return item.country || '';
    case 'eventtype':
    case 'eventtypeid':
    case 'typeofevent':
      return item.eventType || '';
    case 'businessowner':
    case 'businessownerid':
      return item.businessOwner || '';
    case 'numberofattendees':
      return item.attendeeCount || 0;
    default:
      return undefined;
  }
};

const buildLegacyEventDetailAliases = (item, metadataAttributes) =>
  (metadataAttributes || []).flatMap(metadata => {
    const definitionId = metadata?.id ?? metadata?.ID;
    const itemAttribute = (item.attributes || []).find(
      attribute => `${attribute.attributeDefinitionId}` === `${definitionId}`,
    );
    const value = itemAttribute
      ? toLegacyAttributeTableValue(itemAttribute)
      : getEventDetailStaticValue(item, metadata);

    if (!metadata?.key || value === undefined) {
      return [];
    }

    return [buildTableRowItem({ key: metadata.key, value })];
  });

const buildLegacyEventDetailResponse = response => {
  const item = response?.item;
  const metadataAttributes = response?.attributes || [];

  return {
    eventDetails: item
      ? [
          ...buildLegacyEventTableRow(item, { includeCreatedBy: true }),
          ...buildLegacyEventDetailAliases(item, metadataAttributes),
        ]
      : [],
    categoryList: response?.categoryList || [],
    attributes: metadataAttributes,
  };
};

const buildProfileTableResponse = response => {
  const items = response?.items || [];
  const columns = buildColumnsFromResponse({
    response,
    items,
    fallbackColumns: DEFAULT_PROFILE_COLUMNS,
  });

  const tableData = items.map(item => [
    buildTableRowItem({ key: 'KolID', value: item.id }),
    buildTableRowItem({ key: 'profileID', value: item.id }),
    buildTableRowItem({ key: 'profileName', value: item.name || '' }),
    buildTableRowItem({ key: 'KOLName', value: item.name || '' }),
    buildTableRowItem({ key: 'FirstName', value: item.firstName || '' }),
    buildTableRowItem({ key: 'LastName', value: item.lastName || '' }),
    buildTableRowItem({ key: 'CountryID', id: item.countryId, value: item.country || '' }),
    buildTableRowItem({ key: 'CountryFeeCap', value: item.countryFeeCap || '' }),
    buildTableRowItem({ key: 'Kolemail', value: item.email || '' }),
    buildTableRowItem({ key: 'ProfileImage', value: item.profileImageUrl || '' }),
    buildTableRowItem({ key: 'KOLEvents', value: item.eventCount || 0 }),
    buildTableRowItem({ key: 'interactions', value: item.interactionCount || 0 }),
    buildTableRowItem({ key: 'totalEngagements', value: item.totalEngagements || 0 }),
    buildTableRowItem({ key: 'KOLHours', value: item.hours || 0 }),
    buildTableRowItem({
      key: 'KOLHcpMaxEngagements',
      value: item.countryMaxEngagements || 0,
      tooltip: item.countryMaxEngagementTooltip || '',
    }),
    buildTableRowItem({ key: 'EventConflicts', value: item.conflictCount || 0 }),
    ...(item.attributes || []).map(attribute =>
      buildTableRowItem({
        key: toLegacyAttributeKey(attribute),
        value: toLegacyAttributeTableValue(attribute),
      }),
    ),
  ]);

  return { columns, tableData };
};

const DEFAULT_INTERACTION_LIST_COLUMNS = [
  { key: 'Profiles', label: 'Profiles', dataType: 'String', sortOrder: 0 },
  {
    key: 'InteractionTypeID',
    label: 'Type of Interaction',
    dataType: 'dbo.InteractionType',
    sortOrder: 1,
  },
  {
    key: 'InteractionStatusID',
    label: 'Interaction Status',
    dataType: 'dbo.InteractionStatus',
    sortOrder: 2,
  },
  { key: 'BusinessOwnerID', label: 'Business Owner', dataType: 'dbo.GKPUser', sortOrder: 3 },
  { key: 'InteractionDueDate', label: 'Planned Date', dataType: 'Date', sortOrder: 4 },
  { key: 'InteractionActualDate', label: 'Actual Date', dataType: 'Date', sortOrder: 5 },
  { key: 'InteractionNotes', label: 'Notes', dataType: 'TextArea', sortOrder: 6 },
];

const buildLegacyInteractionListRow = item => [
  buildTableRowItem({ key: 'InteractionID', value: item.id }),
  buildTableRowItem({ key: 'KolID', value: item.profiles?.[0]?.id || '' }),
  buildTableRowItem({
    key: 'Profiles',
    value: (item.profiles || []).map(profile => profile.name).join(', '),
  }),
  buildTableRowItem({
    key: 'InteractionTypeID',
    id: item.interactionTypeId,
    value: item.interactionType || '',
  }),
  buildTableRowItem({
    key: 'InteractionStatusID',
    id: item.interactionStatusId,
    value: item.interactionStatus || '',
  }),
  buildTableRowItem({
    key: 'BusinessOwnerID',
    id: item.businessOwnerId,
    value: item.businessOwner || '',
  }),
  buildTableRowItem({ key: 'InteractionDueDate', value: item.plannedDate || '' }),
  buildTableRowItem({ key: 'InteractionActualDate', value: item.actualDate || '' }),
  buildTableRowItem({ key: 'InteractionNotes', value: item.notes || '' }),
  ...(item.attributes || []).map(attribute =>
    buildTableRowItem({
      key: toLegacyAttributeKey(attribute),
      value: toLegacyAttributeTableValue(attribute),
    }),
  ),
];

const buildInteractionListTableResponse = response => {
  const items = response?.items || [];
  const columns = buildColumnsFromResponse({
    response,
    items,
    fallbackColumns: DEFAULT_INTERACTION_LIST_COLUMNS,
  });

  return { columns, tableData: items.map(item => buildLegacyInteractionListRow(item)) };
};

const toLegacyChartResponse = response =>
  (response?.buckets || []).map(bucket => ({
    id: bucket.id,
    name: bucket.label,
    value: bucket.value,
  }));

const buildLegacyChartKey = type => {
  const normalizedType = normalizeType(type);

  return normalizedType === 'Event' ? 'engagements' : 'profile';
};

const buildLegacyChartCatalogResponse = (response, request) => {
  const legacyType = normalizeType(request?.data?.type);
  const chartKeyPrefix = buildLegacyChartKey(legacyType);

  return {
    chartAttributes: (response?.dimensions || []).map(dimension => {
      const resetFilter = `${chartKeyPrefix}_${`${dimension.label || ''}`.replace(/\s+/g, '')}`;

      return {
        filterName: dimension.label,
        filterKey: resetFilter,
        value: dimension.label,
        label: dimension.label,
        sortOrder: dimension.sortOrder,
        apiConfig: {
          method: 'POST',
          url: '/GlobalKOLPlanningShared/GetSummaryChart',
          data: {
            metric: dimension.label,
            type: legacyType,
          },
          resetFilter,
        },
      };
    }),
  };
};

const toLegacyFilterValue = label => `${label || ''}`.replace(/\s+/g, '');

const buildLegacyFilterReferenceElements = (options, attributeDefinitionId = 0) =>
  (options || [])
    .map(option => ({
      id: option?.id ?? 0,
      attributeDefinitionId,
      reference: option?.label || option?.value || '',
    }))
    .filter(option => option.reference);

const resolveLegacyFilterType = (dimension, overrides = {}) => {
  const optionCount = (overrides.options || dimension?.options || []).length;
  const normalizedKey = normalizeKey(overrides.value || dimension?.key || dimension?.label);
  const normalizedDataType = normalizeKey(dimension?.dataType);

  if (overrides.subMenuType) {
    return overrides.subMenuType;
  }

  if (normalizedKey === 'profilename') {
    return 'list';
  }

  if (normalizedDataType === 'date' || normalizedDataType === 'daterange') {
    return 'date';
  }

  if (optionCount > 0) {
    return 'list';
  }

  if (normalizedDataType === 'string') {
    return 'text';
  }

  return 'list';
};

const buildLegacyFilterItem = (dimension, overrides = {}) => {
  const label = overrides.label || dimension?.label || '';
  const value = overrides.value || toLegacyFilterValue(label);
  const options = overrides.options || dimension?.options || [];
  const dimensionKey = overrides.dimensionKey || toStableDimensionKey(dimension);
  const populationScope = overrides.populationScope || toPopulationScope(dimension?.scope);

  return {
    id: overrides.id ?? dimension?.id ?? 0,
    value,
    label,
    dimensionKey,
    populationScope,
    referenceElements: buildLegacyFilterReferenceElements(
      options,
      overrides.attributeDefinitionId ?? dimension?.id ?? 0,
    ),
    subMenu: {
      type: resolveLegacyFilterType(dimension, {
        ...overrides,
        value,
        options,
      }),
    },
  };
};

const mergeLegacyFilterItems = items => {
  const mergedItems = new Map();

  items.forEach(item => {
    const existingItem = mergedItems.get(item.label);

    if (!existingItem) {
      mergedItems.set(item.label, item);
      return;
    }

    const mergedReferenceElements = [
      ...existingItem.referenceElements,
      ...item.referenceElements,
    ].filter(
      (reference, index, references) =>
        references.findIndex(
          current =>
            current.reference === reference.reference &&
            current.attributeDefinitionId === reference.attributeDefinitionId,
        ) === index,
    );

    mergedItems.set(item.label, {
      ...existingItem,
      referenceElements: mergedReferenceElements,
    });
  });

  return [...mergedItems.values()];
};

const sortLegacyFilterItems = items =>
  [...(items || [])].sort((left, right) =>
    `${left?.label || ''}`.localeCompare(`${right?.label || ''}`, undefined, {
      sensitivity: 'base',
    }),
  );

const isProfileNameFilterItem = item => normalizeKey(item?.value || item?.label) === 'profilename';

const buildLegacyFilterCatalogResponse = response => {
  const sharedFilters = response?.sharedFilters || [];
  const eventFilters = response?.eventFilters || [];
  const interactionFilters = response?.interactionFilters || [];
  const profileFilters = response?.profileFilters || [];

  const listDimension = sharedFilters.find(dimension => normalizeKey(dimension?.key) === 'list');
  const collectionDimension = sharedFilters.find(
    dimension => normalizeKey(dimension?.key) === 'collection',
  );

  return {
    gkpFilters: [
      {
        header: null,
        id: 0,
        items: [
          buildLegacyFilterItem(listDimension, {
            id: 0,
            label: 'Lists',
            value: 'list',
            subMenuType: 'kolList',
            attributeDefinitionId: 0,
          }),
          buildLegacyFilterItem(collectionDimension, {
            id: 0,
            label: 'Collections',
            value: 'collections',
            subMenuType: 'collections',
            attributeDefinitionId: 0,
          }),
        ],
      },
      {
        header: 'Engagements',
        id: 1,
        items: sortLegacyFilterItems(
          mergeLegacyFilterItems(
            [...eventFilters, ...interactionFilters].map(dimension =>
              buildLegacyFilterItem(dimension),
            ),
          ),
        ),
      },
      {
        header: 'Profile',
        id: 2,
        items: (() => {
          const mergedItems = mergeLegacyFilterItems(
            profileFilters.map(dimension =>
              buildLegacyFilterItem(dimension, {
                label:
                  normalizeKey(dimension?.key || dimension?.label) === 'profilename'
                    ? 'Profile Name'
                    : dimension?.label,
                value:
                  normalizeKey(dimension?.key || dimension?.label) === 'profilename'
                    ? 'ProfileName'
                    : undefined,
                subMenuType:
                  normalizeKey(dimension?.key || dimension?.label) === 'profilename'
                    ? 'list'
                    : undefined,
              }),
            ),
          );
          const profileNameItem = mergedItems.find(isProfileNameFilterItem);
          const sortedItems = sortLegacyFilterItems(
            mergedItems.filter(item => !isProfileNameFilterItem(item)),
          );

          return profileNameItem ? [...sortedItems, profileNameItem] : sortedItems;
        })(),
      },
    ],
  };
};

const buildLegacyConflictResponse = response =>
  (response?.items || []).map(item => ({
    kol: {
      kolId: item.profile?.id,
      kolName: item.profile?.name || '',
      country: item.profile?.country || '',
      kolCountryCap: item.profile?.countryFeeCap || '',
      customAttributes: toCustomAttributes(item.profile?.attributes),
    },
    kolEvents: (item.events || []).map(event => ({
      eventId: event.id,
      eventTitle: event.title || '',
      startDate: event.startDate,
      endDate: event.endDate,
      country: event.country || '',
      isVirtual: (event.attributes || []).some(
        attribute =>
          ['virtual', 'isvirtual'].includes(normalizeKey(attribute.key || attribute.label)) &&
          ['yes', 'true', '1'].includes(normalizeKey(formatDisplayValue(attribute.value))),
      ),
    })),
  }));

const buildLegacyPeriodChartResponse = response => ({
  chartData: (response?.buckets || []).map(bucket => {
    const chartSources = Object.values(
      (bucket?.events || []).reduce((items, eventItem) => {
        const key = `${eventItem?.eventType || 'Events'}`;
        const existingItem = items[key];

        return {
          ...items,
          [key]: existingItem
            ? {
                ...existingItem,
                value: existingItem.value + 1,
              }
            : {
                name: key,
                value: 1,
                bgColor: withHexPrefix(eventItem?.colorHex),
              },
        };
      }, {}),
    );

    return {
      chartKeyValue: bucket.label,
      chartSources,
    };
  }),
});

const identityResponse = response => response;

const routeAdapters = {
  '/GlobalKOLPlanningShared/GetSummary': {
    getRequest: request => {
      const { data = {}, ...rest } = request;
      const { id, entityId, type, ...remainingData } = data;

      return {
        ...rest,
        url: `${GKP_V2_BASE_URL}/summary`,
        version: 2,
        data: {
          ...remainingData,
          type: normalizeType(type),
          entityId: id ?? entityId,
        },
      };
    },
    adaptResponse: response =>
      (response?.metrics || []).map(metric => ({
        label: metric.label,
        value: metric.value,
        url: metric.url,
      })),
  },
  '/GlobalKOLPlanningEvents/GetEvents': {
    getRequest: request => {
      const { data = {}, ...rest } = request;
      const { KolID, ...remainingData } = data;

      return {
        ...rest,
        url: `${GKP_V2_BASE_URL}/events/search`,
        version: 2,
        data: {
          ...remainingData,
          kolId: KolID,
        },
      };
    },
    adaptResponse: buildEventTableResponse,
  },
  '/GlobalKOLPlanningKOLProfiles/GetKOLs': {
    getRequest: request => ({
      ...request,
      url: `${GKP_V2_BASE_URL}/profiles/search`,
      version: 2,
    }),
    adaptResponse: buildProfileTableResponse,
  },
  '/GlobalKOLPlanningInteractions/GetList': {
    getRequest: request => ({
      ...request,
      url: `${GKP_V2_BASE_URL}/interactions/search`,
      version: 2,
    }),
    adaptResponse: buildInteractionListTableResponse,
  },
  '/GlobalKOLPlanningEvents/GetEventsByPeriod': {
    getRequest: request => {
      const { data = {}, ...rest } = request;
      const { periodType, KolID, ...remainingData } = data;

      return {
        ...rest,
        url: `${GKP_V2_BASE_URL}/events/periods`,
        version: 2,
        data: {
          ...remainingData,
          periodType: normalizePeriodType(periodType),
          kolId: KolID,
        },
      };
    },
    adaptResponse: buildLegacyPeriodChartResponse,
  },
  '/GlobalKOLPlanningEvents/GetEventByEventId': {
    getRequest: request => ({
      ...request,
      url: `${GKP_V2_BASE_URL}/events/detail`,
      version: 2,
    }),
    adaptResponse: buildLegacyEventDetailResponse,
  },
  '/GlobalKOLPlanningEvents/GetCalendarEvents': {
    getRequest: request => ({
      ...request,
      url: `${GKP_V2_BASE_URL}/events/calendar`,
      version: 2,
    }),
    adaptResponse: response => ({
      eventDetails: (response?.items || []).map(item => ({
        id: item.eventId,
        eventId: item.eventId,
        eventTitle: item.title || '',
        startDate: item.startDate,
        endDate: item.endDate,
        backgroundColor: item.colorHex || DEFAULT_CALENDAR_COLOR,
        conflicts: item.conflictCount || 0,
        eventType: item.eventType || '',
        eventOwner: item.businessOwner || '',
        eventEntity: item.entity || '',
        eventDepartment: item.department || '',
        eventAttributes: (item.attributes || []).map(attribute => ({
          columnName: attribute.label,
          value: formatDisplayValue(attribute.value),
        })),
      })),
    }),
  },
  '/GlobalKOLPlanningShared/GetAvailableChartAttributes': {
    getRequest: request => {
      const { data = {}, ...rest } = request;
      const { type, ...remainingData } = data;

      return {
        ...rest,
        url: `${GKP_V2_BASE_URL}/charts/catalog`,
        version: 2,
        data: {
          ...remainingData,
          type: normalizeType(type),
        },
      };
    },
    adaptResponse: buildLegacyChartCatalogResponse,
  },
  '/GlobalKOLPlanningShared/GetSummaryChart': {
    getRequest: request => {
      const { data = {}, ...rest } = request;
      const { type, ...remainingData } = data;

      return {
        ...rest,
        url: `${GKP_V2_BASE_URL}/charts/query`,
        version: 2,
        data: {
          ...remainingData,
          type: normalizeType(type),
        },
      };
    },
    adaptResponse: toLegacyChartResponse,
  },
  '/GlobalKOLPlanningShared/GetAllConflicts': {
    getRequest: request => ({
      ...request,
      url: `${GKP_V2_BASE_URL}/conflicts/search`,
      version: 2,
    }),
    adaptResponse: buildLegacyConflictResponse,
  },
  '/GlobalKOLPlanningSearch/FilterReferenceElements': {
    getRequest: request => ({
      ...request,
      url: `${GKP_V2_BASE_URL}/filters/catalog`,
      version: 2,
    }),
    adaptResponse: buildLegacyFilterCatalogResponse,
  },
};

export const adaptGkpLegacyRequest = request => {
  const adapter = routeAdapters[request?.url];

  if (!adapter || adapter.shouldBypass?.(request)) {
    return {
      request,
      adaptResponse: identityResponse,
    };
  }

  return {
    request: adapter.getRequest(request),
    adaptResponse: response => adapter.adaptResponse(response, request),
  };
};
